import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  ChevronRight,
  Clock3,
  Home,
  MessageCircleWarning,
  AlertTriangle,
  X,
} from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axiosClient from "../../api/axiosClient";
import UserMenuDropdown from "../common/UserMenuDropdown";
import { useAuth } from "../../app/providers/AuthProvider";
import { usePlan } from "../../pages/auth/hooks/usePlan";
import SuperAdminBusinessSelector from "./SuperAdminBusinessSelector";
import { subscribeBusinessSelection } from "../../utils/businessSelectionBus";
import { isBusinessScopedPath } from "../../utils/businessScopePaths";
import {
  REMINDER_AUDIENCE_TYPE,
  REMINDER_CHANGED_EVENT,
  deriveReminderAudience,
  isReminderCompleted,
  isReminderPending,
} from "../../pages/CRM/Reminders/reminderConstants";
import { getBreadcrumbs } from "../../utils/breadcrumbUtils";

const ROLE_LABELS = {
  admin: "Super Admin",
  partner: "Business Partner",
  reseller: "Reseller Partner",
  business: "Business",
  staff: "Staff",
  "business.owner": "Business",
  "business.staff": "Staff",
  "platform.super_admin": "Super Admin",
};

const REMINDER_ATTENTION_LAST_READ_KEY = "xbytechat.reminderAttentionLastRead";

export default function Topbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const reminderMenuRef = useRef(null);
  const reminderRequest = useRef(0);
  const [reminderMenuOpen, setReminderMenuOpen] = useState(false);
  const [dismissedBusinessTip, setDismissedBusinessTip] = useState(false);
  const [reminderLoading, setReminderLoading] = useState(false);
  const [reminderLoadError, setReminderLoadError] = useState("");
  const [reminders, setReminders] = useState([]);
  const [bookingLabel, setBookingLabel] = useState("Bookings");

  const auth = useAuth();
  const {
    user,
    isLoading,
    userName,
    business,
    businessId,
    role,
    isPlatformSuperAdmin,
    canSelectManagedBusinessScope,
    isImpersonating,
    restoreAdminSession,

    // Selected business context
    selectedBusinessId,
    selectedBusinessName,
    setSuperAdminBusiness,
    clearSuperAdminBusiness,
  } = auth;

  const { plan, planId, loading: planLoading, error: planError } = usePlan();

  const roleKey = (role || "").toLowerCase();
  const roleLabel = ROLE_LABELS[roleKey] || roleKey || "Unknown";

  // Roles that do NOT require a plan
  const isAdminRole =
    roleKey === "superadmin" ||
    roleKey === "admin" ||
    roleKey === "partner" ||
    roleKey === "reseller";

  // Only enforce plan presence for business/staff
  const planRelevant = !isAdminRole;

  const hasVisiblePlan = planRelevant && !planLoading && !!planId && !!plan;
  const isFreeSetup =
    planRelevant && !planLoading && !planId && !plan && !planError;

  const hasPlanError =
    planRelevant && !planLoading && !!planError && !hasVisiblePlan;

  const shouldShowBusinessTip =
    canSelectManagedBusinessScope &&
    !selectedBusinessId &&
    isBusinessScopedPath(location.pathname || "");

  useEffect(() => {
    setDismissedBusinessTip(false);
  }, [location.pathname, selectedBusinessId]);

  useEffect(() => {
    return subscribeBusinessSelection(detail => {
      if (detail?.reason === "business-context" && detail?.open === false) {
        setDismissedBusinessTip(false);
      }
    });
  }, []);

  // ✅ don’t log during render — log in effect
  useEffect(() => {
    if (!hasPlanError) return;

    // eslint-disable-next-line no-console
    console.warn("[Topbar] Plan load error for current user.", {
      planId,
      planError,
      role: roleKey,
      userName,
    });
  }, [hasPlanError, planId, planError, roleKey, userName]);

  const currentUserId = useMemo(
    () =>
      normalizeGuid(
        user?.id || user?.userId || user?.Id || user?.UserId || null
      ),
    [user]
  );

  const effectiveBusinessId = useMemo(
    () =>
      normalizeGuid(
        isPlatformSuperAdmin ? null : canSelectManagedBusinessScope ? selectedBusinessId : (businessId ||
          business?.id ||
          business?.businessId ||
          business?.Id ||
          business?.BusinessId ||
          user?.businessId ||
          user?.BusinessId ||
          null)
      ),
    [business, businessId, selectedBusinessId, user, canSelectManagedBusinessScope, isPlatformSuperAdmin]
  );

  const bookingPath = location.pathname.startsWith("/app/scheduling");
  const canReadBookings = auth.hasAllAccess || auth.can?.("SCHEDULING.VIEW");
  useEffect(() => {
    let current = true;
    setBookingLabel("Bookings");
    if (!bookingPath || !canReadBookings || isLoading || !effectiveBusinessId) return undefined;
    axiosClient.get("/scheduling/settings", {
      headers: canSelectManagedBusinessScope ? { "X-Business-Id": effectiveBusinessId } : undefined,
      __silentGenericToast: true, __silent401: true, __silent403: true,
    }).then(response => {
      const settings = response?.data?.data ?? response?.data?.payload ?? response?.data;
      if (current && settings?.menuDisplayLabel) setBookingLabel(settings.menuDisplayLabel);
    }).catch(() => {});
    return () => { current = false; };
  }, [bookingPath, canReadBookings, isLoading, canSelectManagedBusinessScope, effectiveBusinessId]);

  const reminderAttentionReadKey = useMemo(
    () =>
      `${REMINDER_ATTENTION_LAST_READ_KEY}:${effectiveBusinessId || "none"}:${
        currentUserId || "anonymous"
      }`,
    [currentUserId, effectiveBusinessId]
  );

  const [reminderAttentionLastRead, setReminderAttentionLastRead] = useState(
    () => readStorageValue(reminderAttentionReadKey)
  );

  useEffect(() => {
    setReminderAttentionLastRead(readStorageValue(reminderAttentionReadKey));
  }, [reminderAttentionReadKey]);

  // My Work belongs to a signed-in business member, including impersonation.
  // Selecting a managed business does not create that member identity.
  const hasBusinessActor = !canSelectManagedBusinessScope && Boolean(effectiveBusinessId);
  const fetchReminders = useCallback(async () => {
    const request = ++reminderRequest.current;
    if (isLoading || !hasBusinessActor) {
      setReminders([]);
      setReminderLoadError("");
      setReminderLoading(false);
      return;
    }
    try {
      setReminderLoading(true);
      setReminderLoadError("");

      const res = await axiosClient.get("/reminders", {
        headers: canSelectManagedBusinessScope ? { "X-Business-Id": effectiveBusinessId } : undefined,
        __silentGenericToast: true,
        __silent401: true,
        __silent403: true,
      });

      const root = res?.data;
      let list = [];
      if (Array.isArray(root)) {
        list = root;
      } else if (Array.isArray(root?.items)) {
        list = root.items;
      } else if (Array.isArray(root?.data)) {
        list = root.data;
      } else if (Array.isArray(root?.data?.items)) {
        list = root.data.items;
      }

      if (request === reminderRequest.current) setReminders(Array.isArray(list) ? list : []);
    } catch (err) {
      if (request === reminderRequest.current) {
        setReminders([]);
        setReminderLoadError("Couldn't load reminders.");
      }
    } finally {
      if (request === reminderRequest.current) setReminderLoading(false);
    }
  }, [isLoading, hasBusinessActor, canSelectManagedBusinessScope, effectiveBusinessId]);

  useEffect(() => {
    setReminders([]);
    fetchReminders();
    return () => { reminderRequest.current += 1; };
  }, [fetchReminders, location.pathname]);

  useEffect(() => {
    if (!reminderMenuOpen) return;
    fetchReminders();
  }, [fetchReminders, reminderMenuOpen]);

  useEffect(() => {
    const handleReminderChanged = () => {
      fetchReminders();
    };

    window.addEventListener(REMINDER_CHANGED_EVENT, handleReminderChanged);
    return () =>
      window.removeEventListener(REMINDER_CHANGED_EVENT, handleReminderChanged);
  }, [fetchReminders]);

  useEffect(() => {
    const handleClickOutside = event => {
      if (
        reminderMenuRef.current &&
        !reminderMenuRef.current.contains(event.target)
      ) {
        setReminderMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const reminderAttention = useMemo(() => {
    const now = new Date();
    const overdueInternal = [];
    const dueToday = [];
    const failedWhatsApp = [];

    reminders.forEach(reminder => {
      const dueAt = parseDate(reminder?.dueAt);
      const completed = isReminderCompleted(reminder?.status);
      const audienceType = deriveReminderAudience(reminder);
      const ownerUserId = normalizeGuid(
        reminder?.ownerUserId || reminder?.OwnerUserId || null
      );
      const deliveryStatus = String(
        reminder?.deliveryStatus || reminder?.DeliveryStatus || ""
      )
        .trim()
        .toLowerCase();
      const belongsToCurrentUser = ownerUserId && ownerUserId === currentUserId;
      const isOwnerless = !ownerUserId;

      if (
        audienceType === REMINDER_AUDIENCE_TYPE.INTERNAL &&
        belongsToCurrentUser &&
        dueAt &&
        dueAt < now &&
        isReminderPending(reminder?.status)
      ) {
        overdueInternal.push(reminder);
      }

      if (
        belongsToCurrentUser &&
        dueAt &&
        !completed &&
        isSameLocalDay(dueAt, now)
      ) {
        dueToday.push(reminder);
      }

      if (
        audienceType === REMINDER_AUDIENCE_TYPE.CONTACT_WHATSAPP &&
        deliveryStatus === "failed" &&
        (belongsToCurrentUser || isOwnerless)
      ) {
        failedWhatsApp.push(reminder);
      }
    });

    const urgentReminders = [
      ...overdueInternal
        .slice()
        .sort((left, right) => sortByDateAsc(left?.dueAt, right?.dueAt)),
      ...failedWhatsApp
        .slice()
        .sort((left, right) =>
          sortByDateDesc(
            left?.deliveryAttemptedAtUtc || left?.dueAt,
            right?.deliveryAttemptedAtUtc || right?.dueAt
          )
        ),
    ].slice(0, 3);

    return {
      overdueInternal,
      dueToday,
      failedWhatsApp,
      urgentReminders,
      badgeCount: overdueInternal.length + failedWhatsApp.length,
      latestAttentionMarker: getLatestReminderAttentionMarker([
        ...overdueInternal,
        ...dueToday,
        ...failedWhatsApp,
      ]),
    };
  }, [currentUserId, reminders]);

  const hasUnreadReminderAttention = isMarkerNewer(
    reminderAttention.latestAttentionMarker,
    reminderAttentionLastRead
  );

  const markReminderAttentionRead = useCallback(() => {
    const latestMarker = reminderAttention.latestAttentionMarker;
    if (
      !isMarkerNewer(latestMarker, reminderAttentionLastRead) &&
      reminderAttentionLastRead
    ) {
      return;
    }
    if (!latestMarker) return;

    writeStorageValue(reminderAttentionReadKey, latestMarker);
    setReminderAttentionLastRead(latestMarker);
  }, [
    reminderAttention.latestAttentionMarker,
    reminderAttentionLastRead,
    reminderAttentionReadKey,
  ]);

  useEffect(() => {
    if (!reminderMenuOpen) return;
    markReminderAttentionRead();
  }, [markReminderAttentionRead, reminderMenuOpen]);

  const handleOpenReminders = () => {
    markReminderAttentionRead();
    setReminderMenuOpen(false);
    navigate("/app/crm/my-work?view=today");
  };

  const breadcrumbs = getBreadcrumbs(location.pathname, auth, bookingLabel) || [];
  const hideBreadcrumbs = location.pathname.startsWith("/app/inbox/chatinbox");
  const showBreadcrumbs = !hideBreadcrumbs && breadcrumbs.length >= 2;

  return (
    <header
      className={[
        "sticky top-0 z-50 border-b border-gray-100 shadow-[0_2px_6px_rgba(15,23,42,0.06)] transition-all duration-500",
        isImpersonating
          ? ""
          : "h-[var(--app-shell-topbar-size,var(--app-shell-chrome-size))]",
      ].join(" ")}
      style={{
        backgroundColor: "white",
      }}
    >
      {isImpersonating ? (
        <div className="border-b border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <div className="mx-auto flex max-w-[1600px] items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2">
              <AlertTriangle size={16} className="shrink-0 text-amber-700" />
              <span className="truncate font-medium">
                Impersonating:{" "}
                {business?.companyName || business?.businessName || "Business"} (
                {user?.email || "unknown"})
              </span>
            </div>
            <button
              type="button"
              onClick={() => restoreAdminSession()}
              className="shrink-0 rounded-md border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold text-amber-900 hover:bg-amber-100"
            >
              Return to Admin
            </button>
          </div>
        </div>
      ) : null}
      <div
        className={[
          "pl-2 pr-4 lg:pl-4 lg:pr-8",
          isImpersonating
            ? "h-[var(--app-shell-topbar-size,var(--app-shell-chrome-size))]"
            : "h-full",
        ].join(" ")}
      >
        <div className="flex h-full items-center justify-between gap-6">
          {/* Unified Brand Lockup */}
          <div className="flex min-w-0 items-center gap-4">
            <button
              type="button"
              onClick={() => navigate("/app/welcomepage")}
              className="flex shrink-0 items-center gap-0.5 hover:opacity-90 transition-all duration-300 group"
              title="XploreByte"
            >
              <img
                src="/logo/xplorebyte-icon.svg"
                alt="XploreByte"
                className="h-14 w-14 p-2.5 transition-transform duration-300 group-hover:scale-105 object-contain"
              />
              <span className="text-[18px] font-semibold font-brand leading-[24px] text-[#111827] tracking-tight">
                XploreByte
              </span>
            </button>

            {showBreadcrumbs ? (
              <div className="hidden min-w-0 items-center gap-3 font-['Plus_Jakarta_Sans'] md:flex">
                <div className="h-5 w-px bg-slate-200" />

                <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-0.5 overflow-x-auto no-scrollbar text-[11px] font-medium tracking-tight text-slate-400">
                  {breadcrumbs[0].path ? (
                    <Link to={breadcrumbs[0].path} className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 transition-all hover:bg-emerald-50 hover:text-emerald-600" title="Home" aria-label="Home"><Home size={13} /></Link>
                  ) : (
                    <span title="Home" aria-label="Home" className="inline-flex h-7 w-7 items-center justify-center"><Home size={13} /></span>
                  )}

                  {breadcrumbs.slice(1).map((breadcrumb, index) => (
                    <div
                      key={`${breadcrumb.path}-${index}`}
                      className="flex shrink-0 items-center gap-0.5"
                    >
                      <ChevronRight size={11} className="mx-1 text-slate-300" />
                      {breadcrumb.path ? (
                        <Link to={breadcrumb.path} className="rounded-full px-3 py-1 text-slate-400 transition-all hover:text-emerald-600 hover:bg-emerald-50/40">{breadcrumb.label}</Link>
                      ) : (
                        <span className={`rounded-full px-3 py-1 ${breadcrumb.isActive ? "bg-emerald-50 text-emerald-600 font-bold shadow-sm ring-1 ring-emerald-100/50" : "text-slate-400"}`} aria-current={breadcrumb.isActive ? "page" : undefined}>{breadcrumb.label}</span>
                      )}
                    </div>
                  ))}
                </nav>
              </div>
            ) : null}
          </div>

          {/* Right Side - User Info & Actions */}
          <div className="flex items-center gap-4 ml-auto">
            {/* User Info */}
            <div className="hidden md:flex items-center gap-4">
              <div className="text-right">
                <div className="text-xs text-gray-600">Welcome</div>
                <div className="text-sm font-medium text-gray-900 truncate max-w-[150px]">
                  {userName || "User"}
                </div>
              </div>
              {/* ✅ Managed Business Selector */}
              {canSelectManagedBusinessScope && (
                <div className="relative">
                  <SuperAdminBusinessSelector
                    selectedId={selectedBusinessId}
                    selectedName={selectedBusinessName}
                    onSelect={b => setSuperAdminBusiness(b)}
                    onClear={() => clearSuperAdminBusiness()}
                  />

                  {shouldShowBusinessTip && !dismissedBusinessTip && (
                    <div className="pointer-events-none absolute right-0 top-full z-30 mt-2 w-[min(100vw-2rem,16rem)]">
                      <div className="pointer-events-auto relative rounded-[9px] border border-amber-200 bg-amber-50/95 px-3 py-2 shadow-md backdrop-blur">
                        <span className="absolute -top-1.5 right-8 h-3 w-3 rotate-45 border-l border-t border-amber-200 bg-amber-50/95" />
                        <div className="flex items-start gap-2">
                          <div className="mt-0.5 shrink-0 rounded-full bg-amber-100 p-1 text-amber-700">
                            <AlertTriangle size={12} />
                          </div>

                          <div className="min-w-0 flex-1">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <div className="text-xs font-semibold text-slate-900">
                                  Select a business
                                </div>
                                <div className="mt-0.5 text-[11px] leading-4 text-slate-600">
                                  Select one from the topbar when you need
                                  business data.
                                </div>
                              </div>

                              <button
                                type="button"
                                onClick={() => setDismissedBusinessTip(true)}
                                className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-slate-500 hover:bg-white hover:text-slate-700"
                                aria-label="Dismiss business selector tip"
                                >
                                <X size={12} />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Dynamic Plan Badge - Shows actual assigned plan */}
              {hasVisiblePlan && (
                <div
                  className="inline-flex items-center rounded-full px-3 py-1.5 text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 shadow-sm capitalize"
                  title={`Current Plan: ${plan}`}
                  aria-label={`Plan: ${plan}`}
                >
                  {plan}
                </div>
              )}

              {/* Free setup mode badge -> Show as 'Plan: Basic' */}
              {planRelevant && !planLoading && isFreeSetup && (
                <div
                  className="inline-flex items-center rounded-full px-3 py-1.5 text-xs font-medium bg-sky-50 text-sky-700 border border-sky-200 shadow-sm"
                  title="Your current plan is Basic"
                  aria-label="Plan: Basic"
                >
                  Basic
                </div>
              )}

              {/* Error badge only if real plan error */}
              {planRelevant && !planLoading && hasPlanError && (
                <div
                  className="inline-flex items-center rounded-full px-3 py-1.5 text-xs font-medium bg-red-50 text-red-700 border border-red-200 shadow-sm"
                  title="There was an error loading your plan. Please contact support."
                  aria-label="Plan error"
                >
                  Plan Error
                </div>
              )}
            </div>

            {hasBusinessActor ? <div className="relative" ref={reminderMenuRef}>
              <button
                type="button"
                title={
                  hasUnreadReminderAttention
                    ? "My Work, new attention items"
                    : "My Work"
                }
                aria-label={
                  hasUnreadReminderAttention
                    ? "My Work, new attention items"
                    : "My Work"
                }
                onClick={() => setReminderMenuOpen(prev => !prev)}
                className="relative p-2.5 rounded-lg text-gray-600 hover:text-sapphire-600 hover:bg-sapphire-50 transition-all duration-200 group"
              >
                <Clock3 size={20} />
                {hasUnreadReminderAttention ? (
                  <span className="absolute right-1.5 top-1.5 flex h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-white">
                    <span className="sr-only">New reminder attention items</span>
                  </span>
                ) : null}
              </button>

              {reminderMenuOpen ? (
                <div className="absolute right-0 mt-2 w-80 bg-white border border-slate-200 rounded-xl shadow-lg z-50 animate-fadeIn overflow-hidden">
                  <div className="px-4 py-3 border-b border-slate-100">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="text-sm font-semibold text-slate-800">
                          My Work
                        </div>
                        <div className="text-[11px] text-slate-500">
                          Urgent follow-up attention
                        </div>
                      </div>
                      {reminderLoading ? (
                        <span className="text-[11px] text-slate-400">
                          Refreshing...
                        </span>
                      ) : null}
                    </div>
                  </div>

                  <div className="px-4 py-3 space-y-2">
                    <SummaryRow
                      icon={<AlertCircle size={14} className="text-amber-600" />}
                      label="Overdue"
                      count={reminderAttention.overdueInternal.length}
                    />
                    <SummaryRow
                      icon={<Clock3 size={14} className="text-slate-600" />}
                      label="Due Today"
                      count={reminderAttention.dueToday.length}
                    />
                    <SummaryRow
                      icon={
                        <MessageCircleWarning
                          size={14}
                          className="text-rose-600"
                        />
                      }
                      label="Failed WhatsApp"
                      count={reminderAttention.failedWhatsApp.length}
                    />
                  </div>

                  <div className="border-t border-slate-100 px-4 py-3">
                    {reminderLoadError ? (
                      <div className="text-xs text-rose-600">{reminderLoadError}</div>
                    ) : reminderAttention.urgentReminders.length === 0 ? (
                      <div className="text-xs text-slate-500">
                        No urgent follow-ups.
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {reminderAttention.urgentReminders.map(reminder => {
                          const audienceType = deriveReminderAudience(reminder);
                          const isFailedWhatsApp =
                            audienceType ===
                              REMINDER_AUDIENCE_TYPE.CONTACT_WHATSAPP &&
                            String(
                              reminder?.deliveryStatus ||
                                reminder?.DeliveryStatus ||
                                ""
                            )
                              .trim()
                              .toLowerCase() === "failed";

                          return (
                            <div
                              key={reminder?.id}
                              className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2"
                            >
                              <div className="text-xs font-semibold text-slate-800 truncate">
                                {reminder?.title || "Untitled"}
                              </div>
                              <div className="mt-1 text-[11px] text-slate-500 truncate">
                                {isFailedWhatsApp
                                  ? reminder?.deliveryErrorMessage ||
                                    "Delivery failed."
                                  : `Due ${formatDate(reminder?.dueAt)}`}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="border-t border-slate-100 px-4 py-3">
                    <button
                      type="button"
                      onClick={handleOpenReminders}
                      className="w-full inline-flex items-center justify-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-100 transition-colors"
                    >
                      <CheckCircle2 size={16} />
                      Open My Work
                    </button>
                  </div>
                </div>
              ) : null}
            </div> : null}

            {/* User Menu */}
            <UserMenuDropdown
              currentPlan={hasVisiblePlan ? plan : isFreeSetup ? "Basic" : ""}
              roleLabel={roleLabel}
            />
          </div>
        </div>

      </div>
    </header>
  );
}

function SummaryRow({ icon, label, count }) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
      <div className="inline-flex items-center gap-2 text-xs font-medium text-slate-700">
        {icon}
        <span>{label}</span>
      </div>
      <span className="text-xs font-semibold text-slate-900">{count}</span>
    </div>
  );
}

function normalizeGuid(value) {
  return value ? String(value).trim().toLowerCase() : null;
}

function parseDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function isSameLocalDay(left, right) {
  return (
    left.getFullYear() === right.getFullYear() &&
    left.getMonth() === right.getMonth() &&
    left.getDate() === right.getDate()
  );
}

function sortByDateAsc(left, right) {
  const leftDate = parseDate(left);
  const rightDate = parseDate(right);
  if (!leftDate && !rightDate) return 0;
  if (!leftDate) return 1;
  if (!rightDate) return -1;
  return leftDate.getTime() - rightDate.getTime();
}

function sortByDateDesc(left, right) {
  const leftDate = parseDate(left);
  const rightDate = parseDate(right);
  if (!leftDate && !rightDate) return 0;
  if (!leftDate) return 1;
  if (!rightDate) return -1;
  return rightDate.getTime() - leftDate.getTime();
}

function formatDate(value) {
  const date = parseDate(value);
  return date ? date.toLocaleString() : "-";
}

function readStorageValue(key) {
  try {
    return window.localStorage.getItem(key) || "";
  } catch {
    return "";
  }
}

function writeStorageValue(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Browser storage can be unavailable in private or restricted contexts.
  }
}

function getLatestReminderAttentionMarker(reminders) {
  const markers = new Map();

  reminders.forEach(reminder => {
    const id = String(reminder?.id || reminder?.Id || "").trim();
    const marker = getReminderAttentionMarker(reminder);
    if (!marker) return;
    markers.set(id || marker, marker);
  });

  const sortedMarkers = Array.from(markers.values()).sort(compareAttentionMarkers);
  return sortedMarkers[sortedMarkers.length - 1] || "";
}

function getReminderAttentionMarker(reminder) {
  const id = String(reminder?.id || reminder?.Id || "").trim();
  const dateValues = [
    reminder?.updatedAt ||
      reminder?.UpdatedAt,
    reminder?.deliveryAttemptedAtUtc ||
      reminder?.DeliveryAttemptedAtUtc,
    reminder?.dueAt ||
      reminder?.DueAt,
    reminder?.createdAt ||
      reminder?.CreatedAt,
  ];
  const time = dateValues.reduce((latest, value) => {
    const date = parseDate(value);
    return date ? Math.max(latest, date.getTime()) : latest;
  }, 0);
  if (!time && !id) return "";

  return `${time.toString().padStart(13, "0")}:${id}`;
}

function isMarkerNewer(candidate, current) {
  if (!candidate) return false;
  if (!current) return true;
  return compareAttentionMarkers(candidate, current) > 0;
}

function compareAttentionMarkers(left, right) {
  return String(left || "").localeCompare(String(right || ""));
}
